import { useMemo } from 'react';
import { useDetailedSimulation } from '../store/detailedSimulation';
import { useRegulatorAPI } from './useRegulatorAPI';
import type { IntersectionDefinition, ColorCode } from '../types/intersection';
import { ColorCode as CC } from '../types/intersection';

interface IntersectionState {
  groupColors: ColorCode[];
  detectorStates: boolean[];
  currentPhase: string;
  cyclePosition: number;
  cycleTime: number;
  isRunning: boolean;
  speed: number;
}

/**
 * Hook that bridges a data source (simulation or API) with intersection map data.
 * Returns computed group colors and detector states for the current phase.
 *
 * @param intersection - The intersection definition with groups and detectors
 * @param dataSource - 'simulation' (default, local) or 'api' (live from backend)
 */
export function useIntersectionState(
  intersection: IntersectionDefinition,
  dataSource: 'simulation' | 'api' = 'simulation',
): IntersectionState {
  // Read from the appropriate store
  const sim = useDetailedSimulation();
  const api = useRegulatorAPI();

  const source = dataSource === 'api' ? api : sim;
  const { currentPhase, cyclePosition, cycleTime, isRunning, speed } = source;

  // When using API, try to use group pcodes directly from the API response
  const apiDrivers = useRegulatorAPI(s => s.drivers);
  const apiActiveId = useRegulatorAPI(s => s.activeDriverId);

  // Compute group colors based on current phase
  const groupColors = useMemo(() => {
    // If API mode and we have group data from the active driver, use pcodes directly
    if (dataSource === 'api' && apiActiveId !== null) {
      const driver = apiDrivers[String(apiActiveId)];
      if (driver && driver.groups && driver.groups.length > 0) {
        // Map API pcode values to ColorCode enum
        const pcodeToColor: Record<number, ColorCode> = {
          0: CC.D, 1: CC.V, 2: CC.A, 3: CC.R, 4: CC.N,
          5: CC.S, 6: CC.J, 7: CC.I, 8: CC.F, 9: CC.C,
          10: CC.P, 11: CC.G, 14: CC.B, 15: CC.H, 16: CC.K,
          22: CC.E, 39: CC.Z,
        };
        return driver.groups.map(g => pcodeToColor[g.pcode] ?? CC.D);
      }
    }

    // Simulation mode: use phase-based color mapping
    return intersection.groups.map(group => {
      const color = group.phaseColors[currentPhase];
      return color ?? CC.D;
    });
  }, [intersection.groups, currentPhase, dataSource, apiDrivers, apiActiveId]);

  // Compute detector states
  const detectorStates = useMemo(() => {
    // If API mode and we have detector data from the active driver
    if (dataSource === 'api' && apiActiveId !== null) {
      const driver = apiDrivers[String(apiActiveId)];
      if (driver && driver.detectors && driver.detectors.length > 0) {
        // Pad to match intersection detector count
        return intersection.detectors.map((_, i) =>
          i < driver.detectors.length ? driver.detectors[i] : false
        );
      }
    }

    // Simulation mode
    return intersection.detectors.map(detector => {
      if (!isRunning) return false;
      if (detector.type === 'loop') {
        if (detector.id <= 2) return currentPhase === 'A' || currentPhase === 'B';
        return currentPhase === 'C' || currentPhase === 'D';
      }
      return false;
    });
  }, [intersection.detectors, currentPhase, isRunning, dataSource, apiDrivers, apiActiveId]);

  return {
    groupColors,
    detectorStates,
    currentPhase,
    cyclePosition,
    cycleTime,
    isRunning,
    speed
  };
}
